import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import ScrollToTopButton from '@components/common/ScrollToTopButton'
import { PageHero, Icon, ICONS, NAVY, GOLD_ICON } from '@components/marketing'

// Typographie appliquée au contenu juridique (HTML édité depuis le back-office ou JSX statique)
const PROSE = `
  max-w-none text-slate-600 leading-relaxed
  [&_h1]:text-2xl [&_h1]:md:text-3xl [&_h1]:font-extrabold [&_h1]:tracking-tight [&_h1]:text-[#002A4E] [&_h1]:mt-12 [&_h1]:mb-4 [&_h1]:first:mt-0
  [&_h2]:scroll-mt-32 [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-extrabold [&_h2]:tracking-tight [&_h2]:text-[#002A4E] [&_h2]:mt-12 [&_h2]:mb-4 [&_h2]:pt-8 [&_h2]:border-t [&_h2]:border-slate-100 [&_h2]:first:mt-0 [&_h2]:first:pt-0 [&_h2]:first:border-0
  [&_h3]:text-lg [&_h3]:font-bold [&_h3]:text-[#002A4E] [&_h3]:mt-6 [&_h3]:mb-2
  [&_p]:mb-4 [&_p]:leading-relaxed
  [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ul]:space-y-1.5
  [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_ol]:space-y-1.5
  [&_li]:leading-relaxed [&_li]:pl-1 [&_li]:marker:text-[#F5B700]
  [&_a]:text-[#B8860B] [&_a]:font-semibold [&_a]:underline [&_a]:underline-offset-2
  [&_strong]:text-[#002A4E] [&_strong]:font-semibold
  [&_blockquote]:border-l-4 [&_blockquote]:border-[#FFD700] [&_blockquote]:bg-amber-50/50 [&_blockquote]:rounded-r-md [&_blockquote]:py-3 [&_blockquote]:pl-5 [&_blockquote]:pr-4 [&_blockquote]:my-6
  [&_img]:rounded-md [&_img]:my-6 [&_img]:max-w-full
  [&_.ql-align-center]:text-center [&_.ql-align-right]:text-right [&_.ql-align-justify]:text-justify
  [&_.ql-indent-1]:pl-8 [&_.ql-indent-2]:pl-16 [&_.ql-indent-3]:pl-24
`

const slugify = (s, i) =>
  `${i + 1}-${s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`

// Nettoie le HTML collé (styles inline, espaces insécables, balises vides)
const cleanLegalHtml = (html) => {
  if (!html) return ''
  return html
    .replace(/\s*style="[^"]*"/gi, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/<(h[1-6]|p)>\s*(<br\s*\/?>)?\s*<\/\1>/gi, '')
}

function LegalLayout({ badge, title, updatedAt, loading, html, children, related }) {
  const contentRef = useRef(null)
  const [toc, setToc] = useState([])
  const [activeId, setActiveId] = useState(null)

  // Construit le sommaire à partir des titres h2 du contenu
  useEffect(() => {
    const root = contentRef.current
    if (!root) return
    const headings = Array.from(root.querySelectorAll('h2'))
    headings.forEach((h, i) => { if (!h.id) h.id = slugify(h.textContent || 'section', i) })
    setToc(headings.map((h) => ({ id: h.id, label: h.textContent })))

    if (!headings.length) return
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]) setActiveId(visible[0].target.id)
      },
      { rootMargin: '-120px 0px -65% 0px' }
    )
    headings.forEach((h) => observer.observe(h))
    return () => observer.disconnect()
  }, [html, loading])

  const updated = updatedAt ? new Date(updatedAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : null

  return (
    <div className="home-font bg-white text-slate-900 overflow-x-hidden">
      <PageHero badge={badge} badgeIcon="doc" title={title}>
        {updated && (
          <p className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-slate-500">
            <Icon d={ICONS.clock} className="w-4 h-4" />
            Dernière mise à jour : {updated}
          </p>
        )}
      </PageHero>

      <section className="pb-24 pt-4">
        <div className="w-[90%] md:w-[80%] mx-auto grid lg:grid-cols-[260px_1fr] gap-10 lg:gap-14">
          {/* Sommaire */}
          <aside className="hidden lg:block lg:sticky lg:top-28 self-start">
            {toc.length > 0 && (
              <>
                <p className="mb-4 text-sm font-bold uppercase tracking-widest text-slate-400">Sommaire</p>
                <nav className="space-y-1 border-l border-slate-200">
                  {toc.map((t) => {
                    const active = activeId === t.id
                    return (
                      <a
                        key={t.id}
                        href={`#${t.id}`}
                        onClick={(e) => { e.preventDefault(); document.getElementById(t.id)?.scrollIntoView({ behavior: 'smooth' }) }}
                        className={`-ml-px block border-l-2 py-1.5 pl-4 text-sm transition-colors ${active ? 'border-[#F5B700] font-semibold' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
                        style={active ? { color: NAVY } : {}}
                      >
                        {t.label}
                      </a>
                    )
                  })}
                </nav>
              </>
            )}

            {related && (
              <div className="mt-8 rounded-md bg-slate-50 p-5">
                <p className="text-sm font-semibold" style={{ color: NAVY }}>Voir aussi</p>
                <Link to={related.to} className="group mt-2 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: '#B8860B' }}>
                  {related.label}
                  <Icon d={ICONS.arrow} className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
                </Link>
              </div>
            )}
          </aside>

          {/* Contenu */}
          <article className="rounded-md border border-slate-200/70 bg-white p-6 sm:p-10 lg:p-12 shadow-sm">
            {loading ? (
              <div className="space-y-4">
                {[60, 100, 90, 95, 40, 100, 85].map((w, i) => (
                  <div key={i} className="h-4 rounded-full skeleton-shimmer" style={{ width: `${w}%` }} />
                ))}
              </div>
            ) : html ? (
              <div ref={contentRef} className={PROSE} dangerouslySetInnerHTML={{ __html: cleanLegalHtml(html) }} />
            ) : children ? (
              <div ref={contentRef} className={PROSE}>{children}</div>
            ) : (
              <div className="py-16 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-md" style={GOLD_ICON}>
                  <Icon d={ICONS.doc} />
                </span>
                <p className="mt-4 font-semibold" style={{ color: NAVY }}>Le contenu sera bientôt disponible.</p>
              </div>
            )}
          </article>
        </div>

        {/* Bandeau contact */}
        <div className="w-[90%] md:w-[80%] mx-auto mt-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 rounded-md p-7 sm:p-10" style={{ background: 'linear-gradient(135deg, #FFF9E6 0%, #FFF3C4 100%)' }}>
            <div className="flex items-start gap-4">
              <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md bg-white" style={{ color: NAVY }}>
                <Icon d={ICONS.chat} />
              </span>
              <div>
                <h2 className="text-xl font-extrabold" style={{ color: NAVY }}>Une question sur ce document ?</h2>
                <p className="mt-1 text-slate-600">Écrivez-nous à <a href="mailto:info@kudhi.net" className="font-semibold underline underline-offset-2" style={{ color: NAVY }}>info@kudhi.net</a>, nous vous répondrons rapidement.</p>
              </div>
            </div>
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-6 py-3.5 font-bold text-white transition-all hover:-translate-y-0.5 hover:shadow-lg"
              style={{ background: NAVY }}
            >
              Nous contacter
              <Icon d={ICONS.arrow} className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={2.2} />
            </Link>
          </div>
        </div>
      </section>

      <ScrollToTopButton />
    </div>
  )
}

export default LegalLayout
